import { motion } from "framer-motion";
import { X } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useVidyoStore } from "@/features/vidyo-call/utils/vidyo-store";
import { cn } from "@/lib/utils";
import { FocusScope } from "@radix-ui/react-focus-scope";

import { CallFeedbackForm } from "./call-feedback.form";
import { DeviceSettings } from "./device-settings";

export function SettingsDrawer() {
  const isOpen = useVidyoStore((s) => s.settingsDrawer.isOpen);
  const closeSettingsDrawer = useVidyoStore((s) => s.settingsDrawer.close);
  const contentContainerRef = useRef<HTMLDivElement>(null);

  const [isFeedbackSubmitted, setIsFeedbackSubmitted] = useState(false);

  // Close settings drawer when escape key is pressed
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeSettingsDrawer();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [closeSettingsDrawer]);

  // Close settings drawer when component unmounts
  useEffect(() => {
    return () => {
      closeSettingsDrawer();
    };
  }, [closeSettingsDrawer]);

  return (
    <FocusScope
      trapped={isOpen}
      loop
      onMountAutoFocus={(e) => {
        e.preventDefault();
      }}
      onUnmountAutoFocus={(e) => {
        e.preventDefault();
      }}
      asChild
    >
      <motion.div
        className={cn(
          "fixed bottom-[var(--action-bar-height)] right-3 top-4 z-10 w-80",
          "rounded-md border border-border bg-background ring-4 ring-card",
        )}
        initial={{ x: 320 + 12 }}
        animate={{ x: isOpen ? 0 : 320 + 16 }} // 16 is the right-4 on this component
        transition={{ duration: 0.4, ease: "easeInOut" }}
        role="dialog"
        aria-hidden={!isOpen}
        aria-labelledby="settings-drawer-title"
        //@ts-expect-error inert is supported only react 19+
        inert={!isOpen ? "" : undefined}
      >
        <div className="flex h-full flex-col" ref={contentContainerRef}>
          <p
            className="px-4 py-4 text-sm text-foreground"
            id="settings-drawer-title"
          >
            Settings
          </p>

          <Button
            variant="ghost"
            size="icon"
            onClick={closeSettingsDrawer}
            className="absolute right-2 top-2"
          >
            <X className="size-4" />
          </Button>
          <Separator className="bg-border" />
          <ScrollArea
            className={`h-[${contentContainerRef.current?.clientHeight}px] flex-1`}
          >
            <Tabs defaultValue="devices" className="w-full p-4">
              <TabsList className="mb-4 w-full justify-start">
                <TabsTrigger value="devices" className="flex-1">
                  Devices
                </TabsTrigger>
                <TabsTrigger value="feedback" className="flex-1">
                  Feedback
                </TabsTrigger>
              </TabsList>
              <TabsContent value="devices">
                <DeviceSettings />
              </TabsContent>
              <TabsContent value="feedback">
                <CallFeedbackForm
                  setIsSubmitted={setIsFeedbackSubmitted}
                  isSubmitted={isFeedbackSubmitted}
                />
              </TabsContent>
            </Tabs>
          </ScrollArea>
        </div>
      </motion.div>
    </FocusScope>
  );
}
